// @givmo/app-admin — the SHARED match-policy body-builder + tolerant policy reader (07-1.4).
//
// Both the onboarding wizard (07-1.1b) and the Match Program page's builder (07-1.4, moved from the
// Settings → Match tab at EP-MNT2) PUT `/admin/company/match_policy` with the EXACT same body shape.
// To keep the two surfaces from drifting, the wire-body builder + the tolerant `match_policy` read
// live here and both import it.
//
// ★ Money on this endpoint is WHOLE-DOLLAR INTEGERS (JSON numbers), not cents and not decimal
// strings — see onboarding-money.ts. Unchecked optional sections are FORCED to `0` / `''` on the
// wire regardless of stale field state (so a value typed then unchecked is never persisted).

import type { CompanyDetailedOut } from '@givmo/api-client';
import { readYearBudgetPerEmployee } from '../onboarding/onboarding-consts';

/** The editable match-policy form state (shared by the wizard + the Match Program builder). */
export interface MatchPolicyForm {
  /** Per-employee calendar-year match budget (whole-dollar integer) or `null`. REQUIRED > 0. */
  budget: number | null;
  /** Aggregate company-wide cap: on/off + (when on) the whole-dollar amount. */
  annualChecked: boolean;
  annualValue: number | null;
  /** Custom eligible-charities language: on/off + (when on) the free text. */
  customChecked: boolean;
  customValue: string;
}

/**
 * The wire body for `companiesUpdateCompanyMatchPolicy` (PUT /admin/company/match_policy). MUST
 * byte-match the onboarding wizard's `mutate({ body })`. Unchecked sections are forced to neutral
 * values (`value: 0` / `value: ''`) so stale field state never leaks onto the wire.
 *
 * Callers must guard `budget > 0` BEFORE calling this (the endpoint requires a positive budget); a
 * `null` budget is coerced to `0` here only as a type-floor — it should never reach the wire.
 */
export function buildMatchPolicyBody(form: MatchPolicyForm): {
  year_budget_per_employee: number;
  annual_donation_limit: { is_checked: boolean; value: number };
  custom_language: { is_checked: boolean; value: string };
} {
  return {
    year_budget_per_employee: form.budget ?? 0,
    annual_donation_limit: {
      is_checked: form.annualChecked,
      value: form.annualChecked ? (form.annualValue ?? 0) : 0,
    },
    custom_language: {
      is_checked: form.customChecked,
      value: form.customChecked ? form.customValue : '',
    },
  };
}

/**
 * Prime a {@link MatchPolicyForm} from a company's existing policy, tolerating the api-client's
 * `MatchPolicyOut | {} | null` union (the open-record `{}` fallback has no typed members). Mirrors
 * the onboarding wizard's `primeForm`.
 */
export function readMatchPolicyForm(company: CompanyDetailedOut): MatchPolicyForm {
  const policy = (company.match_policy ?? {}) as {
    annual_donation_limit?: { is_checked?: boolean; value?: number | null };
    custom_language?: { is_checked?: boolean; value?: string | null };
  };
  const annual = policy.annual_donation_limit;
  const custom = policy.custom_language;
  return {
    budget: readYearBudgetPerEmployee(company),
    annualChecked: annual?.is_checked ?? false,
    annualValue: typeof annual?.value === 'number' ? annual.value : null,
    customChecked: custom?.is_checked ?? false,
    customValue: typeof custom?.value === 'string' ? custom.value : '',
  };
}

/**
 * Read the server-rendered "Match Policy" string + the required-language off the company's policy,
 * tolerating the `{}`/null union. The rendered string is shown read-only (with a copy button); the
 * required language falls back to `GIVMO_MATCH_CRITERIA` when the server omits it.
 */
export function readRenderedPolicy(company: CompanyDetailedOut): {
  matchPolicy: string;
  requiredLanguage: string | null;
} {
  const policy = (company.match_policy ?? {}) as {
    match_policy?: string | null;
    required_language?: string | null;
  };
  return {
    matchPolicy: typeof policy.match_policy === 'string' ? policy.match_policy : '',
    requiredLanguage:
      typeof policy.required_language === 'string' && policy.required_language.length > 0
        ? policy.required_language
        : null,
  };
}

/** The verbatim client guard shown when a Match save is attempted with a zero/non-integer budget. */
export const MATCH_BUDGET_GUARD_MESSAGE =
  'Changes not saved. Please make sure your match budget is greater than zero and uses whole numbers.';

/** True when the budget is a positive whole-dollar integer (the endpoint's hard precondition). */
export function isBudgetValid(budget: number | null): boolean {
  return budget !== null && Number.isInteger(budget) && budget > 0;
}

/** Structural equality of two match-policy forms (drives the builder's dirty-gate). */
export function matchPolicyFormsEqual(a: MatchPolicyForm, b: MatchPolicyForm): boolean {
  return (
    a.budget === b.budget &&
    a.annualChecked === b.annualChecked &&
    a.customChecked === b.customChecked &&
    // The amount/text only matter when the section is checked (unchecked → forced neutral on the
    // wire). `*Checked` equality is asserted above, so one guard per section suffices.
    (!a.annualChecked || a.annualValue === b.annualValue) &&
    (!a.customChecked || a.customValue === b.customValue)
  );
}
